import { useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";
import { Card, CardHeader, CardBody, Button } from "reactstrap";

const DropzoneComponent = ({
  title,
  setFieldValue,
  images,
  errors,
  touched,
  required = true,
  selectedItem = null,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onDrop = (acceptedFiles, rejectedFiles) => {
    // Handle rejected files
    if (rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0];
      if (rejection.errors.some((e) => e.code === "file-too-large")) {
        toast.error("File is too large. Please choose a smaller image.");
      } else if (rejection.errors.some((e) => e.code === "file-invalid-type")) {
        toast.error("Invalid file type. Please upload an image file.");
      } else {
        toast.error("File upload failed. Please try again.");
      }
      return;
    }

    const file = acceptedFiles[0]; // Only one file
    if (file) {
      // Validate file size (e.g., 5MB limit)
      // const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      // if (file.size > maxSize) {
      //   toast.error(
      //     "File is too large. Please choose an image smaller than 5MB."
      //   );
      //   return;
      // }

      const mappedFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });
      setFieldValue("images", [mappedFile]);
      setSelectedIndex(0);
    }
  };

  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      onDrop,
      multiple: false, // Single file only
      accept: {
        "image/*": [".jpeg", ".jpg", ".png", ".gif", ".bmp", ".webp"],
      },
      // maxSize: 5 * 1024 * 1024, // 5MB limit
    });

  const handleRemoveImage = () => {
    // Clean up preview URL to prevent memory leaks
    if (images.length > 0 && images[0].preview) {
      URL.revokeObjectURL(images[0].preview);
    }
    setFieldValue("images", []);
    setSelectedIndex(0);
  };

  // Cleanup preview URLs on component unmount
  useEffect(() => {
    return () => {
      images.forEach((image) => {
        if (image.preview) {
          URL.revokeObjectURL(image.preview);
        }
      });
    };
  }, [images]);

  const getBorderColor = () => {
    if (isDragReject) return "border-danger";
    if (isDragActive) return "border-success";
    if (touched && errors) return "border-danger";
    return "border-secondary";
  };

  const getBackgroundColor = () => {
    if (isDragReject) return "bg-danger bg-opacity-10";
    if (isDragActive) return "bg-success bg-opacity-10";
    return "bg-white";
  };

  return (
    <Card className="mb-3">
      <CardHeader>
        {title}
        {required && <span className="text-danger ms-1">*</span>}
      </CardHeader>
      <CardBody>
     

        {images.length === 0 ? (
          <div
            {...getRootProps()}
            className={`border border-2 border-dashed rounded p-4 text-center cursor-pointer transition-all ${getBorderColor()} ${getBackgroundColor()}`}
            style={{
              minHeight: "200px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <input {...getInputProps()} />
            <div>
              <div className="mb-3">
                <i
                  className={`bi ${
                    isDragActive ? "bi-cloud-arrow-down" : "bi-cloud-upload"
                  } fs-1 ${isDragReject ? "text-danger" : "text-secondary"}`}
                ></i>
              </div>
              <h5 className={isDragReject ? "text-danger" : ""}>
                {isDragReject
                  ? "Invalid file type"
                  : isDragActive
                  ? "Drop image here"
                  : selectedItem
                  ? "Drag & Drop new image here"
                  : "Drag & Drop image here"}
              </h5>
              <p className="text-muted mb-3">
                {isDragReject
                  ? "Please upload a valid image file"
                  : selectedItem
                  ? "Upload a new image to replace current one (JPG, PNG, etc.) • Max 5MB"
                  : "Upload a single image (JPG, PNG, etc.) • Max 5MB"}
              </p>
              <Button
                color={isDragReject ? "danger" : "primary"}
                type="button"
                outline
                disabled={isDragReject}
              >
                {selectedItem ? "Browse New Image" : "Browse File"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <div className="border rounded mb-3 p-2 bg-light position-relative">
              <div className="small text-muted mb-2">
                {selectedItem
                  ? "New Image (will replace current)"
                  : "Selected Image"}
              </div>
              <img
                src={images[0]?.preview}
                alt="Preview"
                className="img-fluid rounded"
                style={{ maxHeight: "300px", objectFit: "contain" }}
                onError={(e) => {
                  console.error("Image preview error:", e);
                  e.target.src =
                    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Crect width='200' height='200' fill='%23f8f9fa'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%236c757d'%3EImage Error%3C/text%3E%3C/svg%3E";
                }}
              />
              <div className="small text-muted mt-2">
                File: {images[0]?.name} ({(images[0]?.size / 1024).toFixed(1)}{" "}
                KB)
              </div>
            </div>
            <div className="d-flex justify-content-center gap-2">
              <Button
                color="danger"
                size="sm"
                type="button"
                onClick={handleRemoveImage}
              >
                <i className="bi bi-trash me-1"></i>
                Remove
              </Button>
              <div {...getRootProps()} className="d-inline-block">
                <input {...getInputProps()} />
                <Button color="secondary" size="sm" type="button" outline>
                  <i className="bi bi-arrow-repeat me-1"></i>
                  Replace
                </Button>
              </div>
            </div>
          </div>
        )}

        {touched && errors && (
          <div className="text-danger small mt-2">
            <i className="bi bi-exclamation-circle me-1"></i>
            {errors}
          </div>
        )}

        {!required && !images.length && (
          <div className="text-info small mt-2">
            <i className="bi bi-info-circle me-1"></i>
            Image upload is optional for updates
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default DropzoneComponent;
